/**
 * Protected Route Component
 * Redirects unauthenticated users to login page
 */

import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading, skipAuthCheck } = useAuth();
  const location = useLocation();

  // Debug logging for render
  console.log('🔍 [ProtectedRoute] Render:', {
    user: !!user,
    loading,
    skipAuthCheck,
    location: location.pathname,
    timestamp: Date.now()
  });

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="h6" color="text.secondary">
          Checking authentication...
        </Typography>
      </Box>
    );
  }

  // Skip auth check during logout process
  if (skipAuthCheck) {
    console.log('🔓 [ProtectedRoute] Skipping auth check during logout');
    return children;
  }

  // Redirect to login if not authenticated
  if (!user) {
    // Use full URL with port in development to prevent port loss
    const isProduction = import.meta.env.PROD;
    const loginUrl = isProduction ? '/login' : 'http://localhost:3000/login';
    
    console.log('🔒 [ProtectedRoute] Redirecting to login:', { 
      isProduction, 
      loginUrl, 
      user: !!user,
      path: location.pathname
    });

    // For development, use window.location.href to preserve port
    if (!isProduction) {
      window.location.href = loginUrl;
      return null;
    }
    
    return (
      <Navigate 
        to={loginUrl} 
        state={{ from: location }} 
        replace 
      />
    );
  }

  // Check admin requirement
  if (requireAdmin && !user.is_admin) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
          textAlign: 'center',
          p: 3
        }}
      >
        <Typography variant="h4" color="error">
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You don't have permission to access this page.
        </Typography>
      </Box>
    );
  }

  // User is authenticated and authorized
  return children;
};

export default ProtectedRoute;